import { enablePush } from '../lib/push';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '../lib/api';
import { useCatalog } from '../lib/catalog';
import { date, maskPhone, minutes, rupees } from '../lib/format';
import { clearQueryCache } from '../lib/query';
import { Link, navigate } from '../lib/router';
import { hasPlus, resetState, setState, toast, useApp, type Quality, type ThemePref, type VideoLang } from '../lib/store';
import { Icon } from '../components/Icon';
import { AppShell } from '../components/Shell';
import { Streak } from '../components/Brand';

function Segmented<T extends string | number>({ id, label, value, options, onChange }: {
  id: string; label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void;
}) {
  return (
    <div className="setting">
      <span className="label" id={id}>{label}</span>
      <div className="segmented" role="radiogroup" aria-labelledby={id}
        style={{ '--seg-n': options.length, '--seg-i': Math.max(0, options.findIndex((o) => o.value === value)) } as CSSProperties}>
        <span className="seg-thumb" aria-hidden="true" />
        {options.map((o) => (
          <button key={String(o.value)} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ id, label, hint, on, onChange }: { id: string; label: string; hint?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="setting setting-row">
      <span><span className="label" id={id}>{label}</span>{hint && <small className="muted">{hint}</small>}</span>
      <button type="button" role="switch" aria-checked={on} aria-labelledby={id} className={'switch' + (on ? ' on' : '')} onClick={() => onChange(!on)}><span /></button>
    </div>
  );
}

function Card({ title, children }: { title?: string; children: ReactNode }) {
  return <section className="acc-card">{title && <h2>{title}</h2>}{children}</section>;
}

export function Account() {
  const { episodes: EPISODES, plan: PLAN } = useCatalog();
  const s = useApp((x) => x);
  const plus = hasPlus(s);
  const [manage, setManage] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!s.session) {
    return (
      <AppShell mobileHeader={false}>
        <div className="page narrow center-stack">
          <h1 className="page-title">Your account</h1>
          <p className="muted">Log in to save shows, track progress and get Plus.</p>
          <Link to="/login?next=/account" className="btn btn-lg btn-primary">Log in or sign up</Link>
        </div>
      </AppShell>
    );
  }

  const prog = Object.entries(s.progress);
  const weekMs = Date.now() - 7 * 86400_000;
  const watchedThisWeek = prog.filter(([, p]) => p.updatedAt > weekMs).reduce((a, [, p]) => a + p.positionS, 0);
  const finished = EPISODES.filter((e) => s.progress[e.id] && s.progress[e.id].positionS >= e.durationS - 5).length;

  const set = (patch: Partial<typeof s.settings>) => setState((x) => ({ settings: { ...x.settings, ...patch } }));

  const cancel = async () => {
    setBusy(true);
    const sub = await api.cancelSubscription();
    const now = Date.now();
    setState((x) => ({
      sub,
      extraNotifs: [{ id: 'c-' + now, kind: 'payment', title: 'AutoPay cancelled', body: `No more payments. You keep Plus until ${date(sub.currentPeriodEnd)}.`, at: now, link: '/account' }, ...x.extraNotifs],
    }));
    setBusy(false);
    setConfirmCancel(false);
    toast('AutoPay cancelled. No more payments will be taken.');
  };

  const enablePush = async (on: boolean) => {
    if (!on) { set({ push: false }); return; }
    try {
      const r = await enablePush();
      if (r === 'granted') { set({ push: true }); toast('Notifications are on'); }
      else toast(r === 'unsupported' ? 'Notifications are not available here.' : 'Notifications are blocked. Turn them on in your phone or browser settings.');
    } catch {
      toast('Notifications are not available here.');
    }
  };

  return (
    <AppShell mobileHeader={false}>
      <div className="page narrow account">
        <div className="profile">
          <span className="avatar lg">{s.session.name[0]}</span>
          <span><b>{s.session.name}</b><small className="muted">{maskPhone(s.session.phone)}</small></span>
        </div>

        <div className="stats">
          <div><b>{Object.keys(s.saves).length}</b><small>Saved shows</small></div>
          <div><b>{minutes(watchedThisWeek)}</b><small>Watched this week</small></div>
          <div><b>{finished}</b><small>Episodes finished</small></div>
        </div>

        <Streak />

        {s.sub ? (
          <Card>
            <div className="plus-card-top">
              <span className="badge badge-plus">{s.sub.status === 'cancelled' ? 'PLUS UNTIL ' + date(s.sub.currentPeriodEnd).toUpperCase() : 'PLUS ACTIVE'}</span>
              <span className="muted small">{PLAN.name}</span>
            </div>
            {s.sub.status === 'cancelled' ? (
              <p>AutoPay is cancelled. No more payments. You keep Plus until <b>{date(s.sub.currentPeriodEnd)}</b>.</p>
            ) : (
              <p>Next payment <b>{rupees(s.sub.nextChargePaise)}</b> on <b>{date(s.sub.currentPeriodEnd)}</b><br /><small className="muted">UPI AutoPay · {s.sub.upiApp} · {s.sub.vpaMasked}</small></p>
            )}
            {s.sub.status !== 'cancelled' && (
              <div className="row-wrap">
                <button type="button" className="btn btn-sm btn-light" onClick={() => setManage(!manage)} aria-expanded={manage}>Manage AutoPay</button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setManage(true); setConfirmCancel(true); }}>Cancel</button>
              </div>
            )}
            {manage && s.sub.status !== 'cancelled' && (
              <div className="manage">
                <dl className="kv">
                  <div><dt>Mandate ID</dt><dd>{s.sub.mandateId}</dd></div>
                  <div><dt>Maximum per month</dt><dd>{rupees(PLAN.pricePaise)}</dd></div>
                  <div><dt>Started</dt><dd>{date(s.sub.startedAt)}</dd></div>
                  <div><dt>Reminder</dt><dd>24 hours before every payment</dd></div>
                </dl>
                {confirmCancel ? (
                  <div className="confirm">
                    <p><b>Cancel AutoPay?</b> No more payments will be taken. You keep Plus until {date(s.sub.currentPeriodEnd)}.</p>
                    <div className="row-wrap">
                      <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={cancel}>{busy ? 'Cancelling…' : 'Yes, cancel AutoPay'}</button>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmCancel(false)}>Keep Plus</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="text-link danger" onClick={() => setConfirmCancel(true)}>Cancel AutoPay</button>
                )}
              </div>
            )}
          </Card>
        ) : (
          <Card>
            <p><b>Get Kuch Naya Plus</b><br /><small className="muted">Every episode of every show, no ads, offline downloads. Cancel any time.</small></p>
            <Link to="/plus" className="btn btn-primary">See Plus</Link>
          </Card>
        )}

        <Card title="Watching">
          <Segmented<VideoLang> id="set-lang" label="Video language" value={s.videoLang} onChange={(v) => setState({ videoLang: v })}
            options={[{ value: 'Hindi', label: 'Hindi' }, { value: 'English', label: 'English' }, { value: 'Both', label: 'Both' }]} />
          <Segmented<Quality> id="set-quality" label="Video quality" value={s.settings.quality} onChange={(v) => set({ quality: v })}
            options={[{ value: 'saver', label: 'Data saver' }, { value: 'auto', label: 'Auto' }, { value: 'hd', label: 'HD 720p' }]} />
          <Toggle id="set-cc" label="Captions" hint="English subtitles on every video" on={s.settings.captions} onChange={(v) => set({ captions: v })} />
          <Toggle id="set-auto" label="Reels auto-scroll" hint="Play the next reel by itself" on={s.settings.autoScroll} onChange={(v) => set({ autoScroll: v })} />
        </Card>

        <Card title="App">
          <Segmented<ThemePref> id="set-theme" label="Theme" value={s.settings.theme} onChange={(v) => set({ theme: v })}
            options={[{ value: 'system', label: 'Phone setting' }, { value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]} />
          <Segmented<100 | 115 | 130> id="set-text" label="Text size" value={s.settings.textScale} onChange={(v) => set({ textScale: v })}
            options={[{ value: 100, label: 'Normal' }, { value: 115, label: 'Large' }, { value: 130, label: 'Larger' }]} />
          <Toggle id="set-push" label="Notifications" hint="New episodes and payment reminders" on={s.settings.push} onChange={enablePush} />
        </Card>

        <nav className="acc-links" aria-label="More">
          <Link to="/library"><Icon name="library" size={20} /> My library<Icon name="right" size={18} /></Link>
          <Link to="/notifications"><Icon name="bell" size={20} /> Notifications<Icon name="right" size={18} /></Link>
          <details>
            <summary><Icon name="rupee" size={20} /> Payments and invoices<Icon name="down" size={18} /></summary>
            {s.sub ? <p className="muted small">{date(s.sub.startedAt)} · {rupees(PLAN.introPricePaise)} · Paid by {s.sub.upiApp} · Invoice sent by email</p> : <p className="muted small">No payments yet.</p>}
          </details>
          <details>
            <summary><Icon name="whatsapp" size={20} /> Help on WhatsApp<Icon name="down" size={18} /></summary>
            <p className="muted small">Message us on WhatsApp at <b className="selectable">[support number]</b>, 9 AM to 9 PM every day.</p>
          </details>
          <details>
            <summary><Icon name="shield" size={20} /> Refund policy<Icon name="down" size={18} /></summary>
            <p className="muted small">If you were charged by mistake, ask within 7 days and we refund it to your bank in 5–7 working days.</p>
          </details>
        </nav>

        <div className="acc-danger">
          <button type="button" className="text-link" onClick={() => { setState({ session: null }); clearQueryCache(); navigate('/'); }}><Icon name="logout" size={18} /> Log out</button>
          {confirmDelete ? (
            <div className="confirm">
              <p><b>Delete your account?</b> This cancels Plus and removes your profile, progress and saved shows. Payment records are kept as the law requires.</p>
              <div className="row-wrap">
                <button type="button" className="btn btn-sm btn-danger" onClick={async () => { try { await api.deleteAccount(); } catch (e) { toast((e as Error).message); return; } resetState(); clearQueryCache(); navigate('/'); }}>Delete my account</button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmDelete(false)}>Keep it</button>
              </div>
            </div>
          ) : (
            <button type="button" className="text-link danger" onClick={() => setConfirmDelete(true)}><Icon name="trash" size={18} /> Delete account</button>
          )}
          <small className="muted">Version 0.1.0 (preview) · Terms · Privacy · Grievance officer</small>
        </div>
      </div>
    </AppShell>
  );
}
